import { useCallback, useMemo } from 'react'
import { logger } from 'shared/lib'

// Define supported social networks as a const assertion for better type safety
export const SUPPORTED_NETWORKS = {
  facebook: 'facebook',
  linkedin: 'linkedin',
  messenger: 'messenger',
  twitter: 'twitter',
  whatsapp: 'whatsapp',
  telegram: 'telegram',
  email: 'email',
} as const

export type SupportedNetwork = keyof typeof SUPPORTED_NETWORKS

// Network configuration type
interface NetworkConfig {
  url: string
  requiresUrl: boolean
  supportsTitle: boolean
  supportsDescription: boolean
  supportsQuote: boolean
  supportsHashtags: boolean
}

// Network configuration with proper URL templates
const NETWORK_CONFIGS: Record<SupportedNetwork, NetworkConfig> = {
  [SUPPORTED_NETWORKS.facebook]: {
    url: 'https://www.facebook.com/sharer/sharer.php?u=@u&title=@t&description=@d&quote=@q&hashtag=@h',
    requiresUrl: true,
    supportsTitle: true,
    supportsDescription: true,
    supportsQuote: true,
    supportsHashtags: true,
  },
  [SUPPORTED_NETWORKS.linkedin]: {
    url: 'https://www.linkedin.com/shareArticle?url=@u&title=@t&summary=@d',
    requiresUrl: true,
    supportsTitle: true,
    supportsDescription: true,
    supportsQuote: false,
    supportsHashtags: false,
  },
  [SUPPORTED_NETWORKS.messenger]: {
    url: 'fb-messenger://share/?link=@u',
    requiresUrl: true,
    supportsTitle: false,
    supportsDescription: false,
    supportsQuote: false,
    supportsHashtags: false,
  },
  [SUPPORTED_NETWORKS.twitter]: {
    url: 'https://twitter.com/intent/tweet?text=@t&url=@u&hashtags=@h&via=@tu',
    requiresUrl: false,
    supportsTitle: true,
    supportsDescription: false,
    supportsQuote: false,
    supportsHashtags: true,
  },
  [SUPPORTED_NETWORKS.whatsapp]: {
    url: 'https://wa.me/?text=@t%20@u',
    requiresUrl: false,
    supportsTitle: true,
    supportsDescription: false,
    supportsQuote: false,
    supportsHashtags: false,
  },
  [SUPPORTED_NETWORKS.telegram]: {
    url: 'https://t.me/share/url?url=@u&text=@t',
    requiresUrl: true,
    supportsTitle: true,
    supportsDescription: false,
    supportsQuote: false,
    supportsHashtags: false,
  },
  [SUPPORTED_NETWORKS.email]: {
    url: 'mailto:?subject=@t&body=@d%0A%0A@u',
    requiresUrl: false,
    supportsTitle: true,
    supportsDescription: true,
    supportsQuote: false,
    supportsHashtags: false,
  },
}

export interface UseShareProps {
  network: SupportedNetwork
  url?: string
  title?: string
  description?: string
  quote?: string
  hashtags?: string | string[]
  twitterUser?: string
  email?: string
}

export interface UseShareReturn {
  shareLink: string
  canShare: boolean
  share: () => void
}

export const useShare = ({
  network,
  url = '',
  title = '',
  description = '',
  quote = '',
  hashtags = '',
  twitterUser = '',
  email = '',
}: UseShareProps): UseShareReturn => {
  // Validate network
  const isValidNetwork = useMemo(() => {
    return network in SUPPORTED_NETWORKS
  }, [network])

  // Validate required parameters
  const validationErrors = useMemo(() => {
    const errors: string[] = []

    if (!isValidNetwork) {
      errors.push(`Unsupported network: ${network}`)
      return errors
    }

    const config = NETWORK_CONFIGS[network]

    if (config.requiresUrl && !url) {
      errors.push('URL is required for this network')
    }

    if (network === SUPPORTED_NETWORKS.email && !email) {
      errors.push('Email address is required for email sharing')
    }

    return errors
  }, [network, url, email, isValidNetwork])

  // Process hashtags
  const processedHashtags = useMemo(() => {
    if (!hashtags) return ''

    const hashtagArray = Array.isArray(hashtags)
      ? hashtags
      : hashtags.split(',').map((tag) => tag.trim())

    return hashtagArray
      .filter((tag) => tag.length > 0)
      .map((tag) => (tag.startsWith('#') ? tag.slice(1) : tag))
      .join(',')
  }, [hashtags])

  // Generate share link
  const shareLink = useMemo(() => {
    if (!isValidNetwork || validationErrors.length > 0) {
      return ''
    }

    const config = NETWORK_CONFIGS[network]
    let link = config.url

    // Handle Twitter-specific logic
    if (network === SUPPORTED_NETWORKS.twitter) {
      if (!processedHashtags) {
        link = link.replace('&hashtags=@h', '')
      }
      if (!twitterUser) {
        link = link.replace('&via=@tu', '')
      }
    }

    // Replace placeholders with actual values
    const replacements: Record<string, string> = {
      '@u': url,
      '@t': title,
      '@d': description,
      '@q': quote,
      '@h': processedHashtags,
      '@tu': twitterUser,
      '@email': email,
    }

    // Apply replacements and encode
    Object.entries(replacements).forEach(([placeholder, value]) => {
      if (value) {
        const encodedValue = encodeURIComponent(value)
        link = link.replace(new RegExp(placeholder, 'g'), encodedValue)
      } else {
        // Remove empty parameters
        link = link.replace(new RegExp(`[?&][^=]*=${placeholder}`, 'g'), '')
      }
    })

    // Clean up any double ampersands or trailing ampersands
    link = link.replace(/&&/g, '&').replace(/[?&]$/, '')

    return link
  }, [
    network,
    url,
    title,
    description,
    quote,
    processedHashtags,
    twitterUser,
    email,
    isValidNetwork,
    validationErrors,
  ])

  const canShare = useMemo(() => {
    return isValidNetwork && validationErrors.length === 0 && shareLink.length > 0
  }, [isValidNetwork, validationErrors, shareLink])

  const share = useCallback(() => {
    if (!canShare) {
      console.warn('Cannot share:', validationErrors.join(', '))
      return
    }

    try {
      // For mobile apps, try to open in new window
      if (network === SUPPORTED_NETWORKS.messenger) {
        window.location.href = shareLink
      } else {
        window.open(shareLink, '_blank', 'noopener,noreferrer')
      }
    } catch (error) {
      logger.error(error)
    }
  }, [canShare, shareLink, network, validationErrors])

  return {
    shareLink,
    canShare,
    share,
  }
}
